import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";

import { AppShell } from "./components/AppShell.js";
import { UpdateBanner } from "./components/UpdateBanner.js";
import {
  requestNotificationPermission,
  signalAppReady,
} from "./lib/notifications/desktopBridge.js";
import { initAppFocusTracking } from "./lib/notifications/appFocus.js";
import { initClientVersion } from "./lib/clientVersion.js";
import { AuthProvider } from "./auth/AuthProvider.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { LandingRoute } from "./auth/LandingRoute.js";
import { useCan } from "./auth/permissions.js";
import { BotsPanel as OttoBotsPanel } from "./pages/otto/BotsPanel.js";
import { ProvidersPanel as AIProvidersPanel } from "./pages/ai-providers/ProvidersPanel.js";
import { SecurityPanel } from "./pages/admin/security/SecurityPanel.js";
import { ChangelogPanel } from "./pages/admin/changelog/ChangelogPanel.js";
import { SystemEmailsPanel } from "./pages/admin/system-emails/SystemEmailsPanel.js";
import { AdminLayout } from "./pages/admin/AdminLayout.js";
import { SystemAdminLayout } from "./pages/admin/SystemAdminLayout.js";
import { CrmRoot } from "./crm/CrmRoot.js";
import { crmRouteElements } from "./crm/CrmRoutes.js";
import { ChatPage } from "./pages/chat/ChatPage.js";
import ProfilePage from "./pages/profile/ProfilePage.js";
import NotificationsPage from "./pages/profile/NotificationsPage.js";
import { AccessPage } from "./pages/documentation/access/AccessPage.js";
import { OnlineTrainingEditorPage } from "./pages/documentation/online-training/OnlineTrainingEditorPage.js";
import { OnlineTrainingListPage } from "./pages/documentation/online-training/OnlineTrainingListPage.js";
import { CategoriesManagePage } from "./pages/documentation/CategoriesManagePage.js";
import { SolutionAreasManagePage } from "./pages/documentation/SolutionAreasManagePage.js";
import { DocumentAiPage } from "./pages/documentation/DocumentAiPage.js";
import { DocumentEditPage } from "./pages/documentation/DocumentEditPage.js";
import { DocumentBulkDeletePage } from "./pages/documentation/DocumentBulkDeletePage.js";
import { DocumentBulkUploadPage } from "./pages/documentation/DocumentBulkUploadPage.js";
import { DocumentManualNewPage } from "./pages/documentation/DocumentManualNewPage.js";
import { DocumentNewPage } from "./pages/documentation/DocumentNewPage.js";
import { DocumentUploadPage } from "./pages/documentation/DocumentUploadPage.js";
import { DocumentationIndex } from "./pages/documentation/DocumentationIndex.js";
import { DocumentationLayout } from "./pages/documentation/DocumentationLayout.js";
import { DocumentationSearchPage } from "./pages/documentation/DocumentationSearchPage.js";
import { LibraryDocPage } from "./pages/documentation/LibraryDocPage.js";
import { TypeReleaseDocsPage } from "./pages/documentation/TypeReleaseDocsPage.js";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage.js";
import { DownloadPage } from "./pages/DownloadPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import Setup2faPage from "./pages/Setup2faPage.js";
import SetPasswordPage from "./pages/SetPasswordPage.js";
import { DraftDetailPage } from "./pages/marketing/DraftDetailPage.js";
import { HistoryPage as MarketingHistoryPage } from "./pages/marketing/HistoryPage.js";
import { MarketingLayout } from "./pages/marketing/MarketingLayout.js";
import { MarketingAgentLayout } from "./pages/marketing/agent/MarketingAgentLayout.js";
import { KnowledgePage as MarketingKnowledgePage } from "./pages/marketing/agent/KnowledgePage.js";
import { StatusPage as MarketingStatusPage } from "./pages/marketing/agent/StatusPage.js";
import { MarketingCampaignsRoot } from "./pages/marketing/campaigns/MarketingCampaignsRoot.js";
import { ColdEmailRoot } from "./pages/marketing/cold-email/ColdEmailRoot.js";
import { MarketingSettingsPage } from "./pages/marketing/settings/MarketingSettingsPage.js";
import { WebinarDetailPage } from "./pages/marketing/webinars/WebinarDetailPage.js";
import { WebinarsListPage } from "./pages/marketing/webinars/WebinarsListPage.js";
import { WorkbenchPage as MarketingWorkbenchPage } from "./pages/marketing/WorkbenchPage.js";
import { ScheduleCalendar as MarketingSchedulePage } from "./pages/marketing/workbench/ScheduleCalendar.js";
// Email-campaign pages live in the CRM sub-app but are surfaced under
// /marketing/campaigns (relocated out of /crm, 2026-06-02). Their routes are
// declared in their own manifest (and pinned by a test) so none can be lost
// again the way the Sendout Logs route was.
import { marketingCampaignRouteElements } from "./pages/marketing/campaigns/campaignRoutes.js";
// Cold Email Agent pages live in the CRM sub-app (they drive crm-api's
// /cold_emails endpoints + read the contacts directory) but are surfaced under
// /marketing/cold-email. Imported via @crm-src so they keep the CRM runtime.
import ColdEmailQueuePage from "@crm-src/pages/cold-email/ColdEmailQueuePage";
import ColdEmailReviewPage from "@crm-src/pages/cold-email/ColdEmailReviewPage";
import { SalesLayout } from "./pages/sales/SalesLayout.js";
import { StatusPage as SalesStatusPage } from "./pages/sales/status/StatusPage.js";
import { LeadPage as SalesLeadPage } from "./pages/sales/leads/LeadPage.js";
import { LeadsPage as SalesLeadsPage } from "./pages/sales/leads/LeadsPage.js";
import { ContactDirectory as SalesContactDirectory } from "./pages/sales/contacts/ContactDirectory.js";
import { CampaignsPage as SalesCampaignsPage } from "./pages/sales/campaigns/CampaignsPage.js";
import { CampaignResultsPage as SalesCampaignResultsPage } from "./pages/sales/campaigns/CampaignResultsPage.js";
import { StrategyPage as SalesStrategyPage } from "./pages/sales/strategy/StrategyPage.js";
import { KnowledgePage as SalesKnowledgePage } from "./pages/sales/knowledge/KnowledgePage.js";
import { ServerStatusPage } from "./pages/ServerStatusPage.js";
import { TesterLayout } from "./pages/TesterLayout.js";
import { TesterRunPage } from "./pages/tester/TesterRunPage.js";
import { TesterServersPage } from "./pages/tester/TesterServersPage.js";
import { TesterHistoryPage } from "./pages/tester/TesterHistoryPage.js";
import { SupportLayout } from "./pages/support/SupportLayout.js";
import { ConversationDetailPage } from "./pages/support/conversations/ConversationDetailPage.js";
import { ConversationsListPage } from "./pages/support/conversations/ConversationsListPage.js";
import { OrganizationDetailPage } from "./pages/support/orgs/OrganizationDetailPage.js";
import { OrganizationsListPage } from "./pages/support/orgs/OrganizationsListPage.js";
import { ReportsPage } from "./pages/support/reports/ReportsPage.js";
import { SettingsLayout } from "./pages/support/settings/SettingsLayout.js";
import { SettingsSlaPanel } from "./pages/support/settings/SettingsSlaPanel.js";
import { SettingsWorkflowsPanel } from "./pages/support/settings/SettingsWorkflowsPanel.js";
import { TicketDetailPage } from "./pages/support/tickets/TicketDetailPage.js";
import { TicketListPage } from "./pages/support/tickets/TicketListPage.js";

void AuthProvider; // ensure Vite keeps the symbol alive when type-only callers come and go

/**
 * Param-preserving redirect for the legacy `/marketing/drafts/:id` deep-link
 * after the AI-Agent area moved under `/marketing/agent` (2026-06-02).
 */
function RedirectToAgentDraft(): JSX.Element {
  const { id } = useParams();
  return <Navigate to={`/marketing/agent/drafts/${id ?? ""}`} replace />;
}

/**
 * `/admin/reseller-applications/:id` → the CRM review page. Reseller
 * Applications moved from System Admin to a tab of CRM ▸ Reseller Management
 * on 2026-09-12; the reviewer emails crm-api sent before that day still carry
 * the old address, so it keeps resolving.
 */
function RedirectToResellerApplication(): JSX.Element {
  const { id } = useParams();
  return <Navigate to={`/crm/administration/reseller-applications/${id ?? ""}`} replace />;
}

/**
 * `/admin` landing. It cannot be a static `<Navigate to="users">` any more:
 * the shell now hosts two surfaces behind different permissions, and an actor
 * that holds `crm:resellers` but not `admin:users` (a sales_manager) would be
 * bounced straight into a Forbidden page by a fixed redirect to the first tab
 * it cannot see. Send everyone to the first tab they actually hold.
 */
function AdminIndexRedirect(): JSX.Element {
  const canAdminUsers = useCan("admin:users");
  const canManageResellers = useCan("crm:resellers");
  // No reseller tab lives here any more (2026-09-12): an actor that holds only
  // `crm:resellers` has nothing in System Admin, so it is sent to the CRM's
  // reseller list — the surface that grant is actually about — instead of a
  // Forbidden page. Everyone else lands on User Management (still the most
  // useful permission for the Forbidden page to name).
  if (!canAdminUsers && canManageResellers) {
    return <Navigate to="/crm/administration/resellers" replace />;
  }
  return <Navigate to="users" replace />;
}

/**
 * Documentation authoring routes (create/edit pages) are reachable by BOTH
 * the create tier (`documentation:create` — author + manage own) and the full
 * editor (`documentation:edit` — manage any). The own-records rule for the
 * create tier is enforced by the edit page + the server proxy, not the route.
 */
const DOC_AUTHORING_PERMS = ["documentation:create", "documentation:edit"] as const;

/**
 * Top-level routing. Every section sits behind <RequireAuth permission>
 * so a missing permission shows the friendly Forbidden screen instead
 * of leaking the inner page.
 */
export function App(): JSX.Element {
  // Tell the Tauri shell the SPA has painted so it can dismiss the splash
  // and reveal the main window (no-op in a plain browser).
  useEffect(() => {
    void signalAppReady();
    // Resolve which client we're on (desktop app version, or "web") so the
    // x-client-version header reports it — System Admin shows each user's build.
    void initClientVersion();
    // Desktop only: ask for OS notification permission up front so the
    // system prompt appears on launch (no-op in the browser / if already
    // granted / on an ad-hoc-signed macOS build that can't register).
    void requestNotificationPermission();
    // Track window focus so live chat messages only raise an OS desktop
    // notification when the app is NOT focused (in use ⇒ in-app snackbar
    // only). No-op wiring on the web (focus is read from the document).
    let stopFocusTracking: (() => void) | undefined;
    void initAppFocusTracking().then((stop) => {
      stopFocusTracking = stop;
    });
    return () => stopFocusTracking?.();
  }, []);

  return (
    <>
      {/* Desktop only: visible "a new version is available → click to
          update" prompt. No-op (renders nothing) in a plain browser. */}
      <UpdateBanner />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public — invited staff set their password here (no session
          yet; the token is the auth). Staff invites link here, not
          the customer portal. */}
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      {/* Public — "get the desktop app" landing (the installer feed is
          public; shareable so staff can grab the native client). */}
      <Route path="/download" element={<DownloadPage />} />
      {/* Forced first-password-change gate (after a one-time invite-password
          login). `allowMustChange` + `allowUnenrolled` so a fresh invitee
          (who carries BOTH flags) can render this page; every OTHER route
          bounces a must-change user here FIRST (before /setup-2fa). */}
      <Route
        path="/set-password"
        element={
          <RequireAuth allowMustChange allowUnenrolled>
            <SetPasswordPage />
          </RequireAuth>
        }
      />
      {/* Mandatory 2FA enrollment gate. `allowUnenrolled` lets an
          unenrolled user reach it without RequireAuth bouncing them into a
          redirect loop; every OTHER route sends must-enroll users here.
          (A must-CHANGE user landing here is still bounced to /set-password
          first — this route does NOT allowMustChange.) */}
      <Route
        path="/setup-2fa"
        element={
          <RequireAuth allowUnenrolled>
            <Setup2faPage />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Index landing. Not every role has the company-pulse dashboard —
            a sales role (no `dashboard:view`) is redirected to its first
            usable section (CRM) instead of a Forbidden page at `/`. See
            `LandingRoute`. */}
        <Route index element={<LandingRoute />} />
        {/* Otto bot management moved INTO System Admin (`/admin/otto`)
            — it's a system-administration surface, not its own
            top-level section. These keep old `/otto` + `/otto/bots`
            bookmarks (and the pre-move embed-injector docs) working. */}
        {/* Self-service profile + 2FA. Auth is the only gate (the
            outer RequireAuth) — every signed-in user manages self. */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="profile/notifications" element={<NotificationsPage />} />
        <Route path="otto" element={<Navigate to="/admin/otto" replace />} />
        <Route
          path="otto/bots"
          element={<Navigate to="/admin/otto" replace />}
        />
        {/* Internal staff team chat — DMs + an "All Staff" room. Gated on
            `chat:view`, which every built-in role carries. */}
        <Route
          path="chat"
          element={
            <RequireAuth permission="chat:view">
              <ChatPage />
            </RequireAuth>
          }
        />
        <Route
          path="support"
          element={
            <RequireAuth permission="support:view">
              <SupportLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="tickets" replace />} />
          <Route path="tickets" element={<TicketListPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="conversations" element={<ConversationsListPage />} />
          <Route path="conversations/:id" element={<ConversationDetailPage />} />
          <Route path="organizations" element={<OrganizationsListPage />} />
          <Route path="organizations/:id" element={<OrganizationDetailPage />} />
          {/* Legacy routes — knowledge moved to /documentation, staff
           * users moved to /admin, workflows folded into Settings. */}
          <Route path="knowledge" element={<Navigate to="/documentation" replace />} />
          <Route path="users" element={<Navigate to="/admin" replace />} />
          <Route path="workflows" element={<Navigate to="settings/workflows" replace />} />
          <Route path="audit" element={<Navigate to="reports" replace />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route
            path="settings"
            element={
              <RequireAuth permission="support:manage">
                <SettingsLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="workflows" replace />} />
            <Route path="workflows" element={<SettingsWorkflowsPanel />} />
            <Route path="sla" element={<SettingsSlaPanel />} />
            {/* Legacy: customer-portal and categories sub-tabs removed;
             * KB categories are managed in /documentation now. Redirect
             * any bookmark to the new default. */}
            <Route path="portal" element={<Navigate to="../workflows" replace />} />
            <Route path="categories" element={<Navigate to="../workflows" replace />} />
          </Route>
        </Route>
        <Route
          path="crm"
          element={
            <RequireAuth permission="crm:view">
              <CrmRoot />
            </RequireAuth>
          }
        >
          {crmRouteElements}
        </Route>
        <Route
          path="sales"
          element={
            <RequireAuth permission="sales:view">
              <SalesLayout />
            </RequireAuth>
          }
        >
          {/* The Leads list is the default landing (2026-06-16). The old
              Today tab was removed: its leads-of-today list is now a "Today"
              filter on the Leads tab, and its agent-status + spend panels
              moved to the new Status tab. Keep old /sales/today bookmarks +
              the dashboard's deep links working by forwarding them to the
              Leads "Today" filter. */}
          <Route index element={<Navigate to="leads" replace />} />
          <Route path="today" element={<Navigate to="/sales/leads?tab=today" replace />} />
          <Route path="status" element={<SalesStatusPage />} />
          <Route path="leads" element={<SalesLeadsPage />} />
          <Route path="contacts" element={<SalesContactDirectory />} />
          <Route path="campaigns" element={<SalesCampaignsPage />} />
          {/* One campaign's results: every company + contact it harvested,
              and the "Create contact group" hand-off to Email Campaigns. */}
          <Route path="campaigns/:slug" element={<SalesCampaignResultsPage />} />
          {/* `/sales/leads/:id` is now a real page (not a drawer over
              the list). Lets the operator export to PDF and read on a
              wide layout instead of the cramped right rail. */}
          <Route path="leads/:id" element={<SalesLeadPage />} />
          <Route path="strategy" element={<SalesStrategyPage />} />
          <Route path="knowledge" element={<SalesKnowledgePage />} />
          {/* The Brain tab was folded into the bottom of Status (2026-08-07)
              — one page answers "is it working / what does it cost / what is
              it doing right now". Keep old /sales/brain links working. */}
          <Route path="brain" element={<Navigate to="/sales/status" replace />} />
        </Route>
        <Route
          path="marketing"
          element={
            <RequireAuth permission="marketing:view">
              <MarketingLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="agent" replace />} />
          {/* AI Agent area — the autonomous marketing agent. Its secondary
              sub-tab strip (Workbench / History / Settings) lives in
              MarketingAgentLayout; the section's primary area switch +
              operator controls live one level up in MarketingLayout. */}
          <Route path="agent" element={<MarketingAgentLayout />}>
            <Route index element={<MarketingWorkbenchPage />} />
            {/* Schedule calendar — moved out of the Workbench into its own
                sub-tab (2026-06-16): month grid of scheduled posts with
                reschedule / cancel. */}
            <Route path="schedule" element={<MarketingSchedulePage />} />
            {/* The cockpit: agent state, spend + sleep, per-loop schedule,
                and the live thinking trace, all on one screen. */}
            <Route path="status" element={<MarketingStatusPage />} />
            {/* The three creation modes (autonomous / ideas / on-demand) moved
                INTO the Workbench's Content studio (2026-06-16). Old per-tab
                URLs deep-link into the right studio tab via ?studio=. */}
            <Route
              path="create"
              element={<Navigate to="/marketing/agent?studio=ondemand" replace />}
            />
            <Route
              path="ideas"
              element={<Navigate to="/marketing/agent?studio=ideas" replace />}
            />
            <Route path="knowledge" element={<MarketingKnowledgePage />} />
            <Route path="history" element={<MarketingHistoryPage />} />
            <Route path="settings" element={<MarketingSettingsPage />} />
            {/* Drafts deep-link kept so press-trigger / thoughts links
                from older bookmarks still work. */}
            <Route path="drafts/:id" element={<DraftDetailPage />} />
          </Route>
          {/* Email Campaigns area — relocated out of /crm (2026-06-02).
              Runs on the CRM runtime (store + crm-api session + websocket)
              via the wrapper; contacts + contact-groups are still read live
              from crm-api. */}
          <Route path="campaigns" element={<MarketingCampaignsRoot />}>
            {marketingCampaignRouteElements}
          </Route>
          {/* Webinars — the five-step event flow (invite → attendees →
              blog/LinkedIn → public survey → leads). Runs on the DASHBOARD
              runtime (its own /api/webinars surface); the campaign editor it
              hands off to at steps 1/2/4 is the CRM-runtime one above. See
              docs/webinars.md. */}
          <Route path="webinars">
            <Route index element={<WebinarsListPage />} />
            <Route path=":id" element={<WebinarDetailPage />} />
          </Route>
          {/* Cold Email Agent — AI-drafted, CEO-approved cold outreach to every
              contact that is NOT a customer or a prospect. Runs on the CRM
              runtime (store + crm-api session) via ColdEmailRoot; crm-api
              enforces all-records access to build and super-admin to send. */}
          <Route path="cold-email" element={<ColdEmailRoot />}>
            <Route index element={<ColdEmailQueuePage />} />
            <Route path=":id" element={<ColdEmailReviewPage />} />
          </Route>
          {/* Legacy /marketing/* URLs → their new homes (old bookmarks +
              existing e2e specs). The agent's own pages moved under
              /marketing/agent; the v2 per-feature routes still fold into
              the Workbench. */}
          <Route path="history" element={<Navigate to="/marketing/agent/history" replace />} />
          <Route path="settings" element={<Navigate to="/marketing/agent/settings" replace />} />
          <Route path="drafts/:id" element={<RedirectToAgentDraft />} />
          <Route path="trends" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="drafts" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="review" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="schedule" element={<Navigate to="/marketing/agent/schedule" replace />} />
          <Route path="thinking" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="knowledge" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="press-triggers" element={<Navigate to="/marketing/agent" replace />} />
          <Route path="how-it-works" element={<Navigate to="/marketing/agent" replace />} />
        </Route>
        {/* `manage_access` is not a tier of `view` — it is a different job —
            but it lives under this section, so holding it has to be enough
            to open the section. Gating the shell on `view` alone would give
            a role granted only access-management a 403 on the one page it
            was granted. */}
        <Route
          path="documentation"
          element={
            <RequireAuth anyOf={["documentation:view", "documentation:manage_access"]}>
              <DocumentationLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DocumentationIndex />} />
          <Route path="search" element={<DocumentationSearchPage />} />
          <Route path="library/:slug" element={<LibraryDocPage />} />
          {/* TWO SHELVES (2026-09-13). `release/:releaseId` is release
           * documentation — everything pinned to that release or version,
           * versioned automatically from release data. `type/:slug` is a
           * LIBRARY folder's unversioned documents. The old FOLDER →
           * RELEASE pair still renders so bookmarks keep working. One page
           * serves all three shapes (see TypeReleaseDocsPage). */}
          <Route path="release/:releaseId" element={<TypeReleaseDocsPage />} />
          <Route path="type/:slug" element={<TypeReleaseDocsPage />} />
          <Route
            path="type/:slug/release/:releaseId"
            element={<TypeReleaseDocsPage />}
          />
          <Route
            path="manage"
            element={<Navigate to="/documentation" replace />}
          />
          <Route
            path="manage/new"
            element={
              <RequireAuth anyOf={DOC_AUTHORING_PERMS}>
                <DocumentNewPage />
              </RequireAuth>
            }
          />
          <Route
            path="manage/new/manual"
            element={
              <RequireAuth anyOf={DOC_AUTHORING_PERMS}>
                <DocumentManualNewPage />
              </RequireAuth>
            }
          />
          <Route
            path="manage/new/upload"
            element={
              <RequireAuth anyOf={DOC_AUTHORING_PERMS}>
                <DocumentUploadPage />
              </RequireAuth>
            }
          />
          <Route
            path="manage/new/bulk"
            element={
              <RequireAuth permission="documentation:edit">
                <DocumentBulkUploadPage />
              </RequireAuth>
            }
          />
          <Route
            path="manage/bulk-delete"
            element={
              <RequireAuth permission="documentation:edit">
                <DocumentBulkDeletePage />
              </RequireAuth>
            }
          />
          <Route
            path="manage/new/ai"
            element={
              <RequireAuth anyOf={DOC_AUTHORING_PERMS}>
                <DocumentAiPage />
              </RequireAuth>
            }
          />
          {/* Edit reachable by both tiers; the page + server enforce the
              own-records rule for create-tier authors (a create-tier user
              who opens someone else's doc gets a read-only notice, and the
              PATCH/DELETE proxy would 403 regardless). */}
          <Route
            path="manage/:slug/edit"
            element={
              <RequireAuth anyOf={DOC_AUTHORING_PERMS}>
                <DocumentEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="categories"
            element={
              <RequireAuth permission="documentation:edit">
                <CategoriesManagePage />
              </RequireAuth>
            }
          />
          <Route
            path="solution-areas"
            element={
              <RequireAuth permission="documentation:edit">
                <SolutionAreasManagePage />
              </RequireAuth>
            }
          />
          {/* Who SEES which documents — gated on documentation:manage_access,
              deliberately not documentation:edit. Writing a document and
              deciding which customer may read it are different jobs. */}
          <Route
            path="access"
            element={
              <RequireAuth permission="documentation:manage_access">
                <AccessPage />
              </RequireAuth>
            }
          />
          {/* Online Training — reseller video courses (2026-11-02). Readers
              see the modules; the editing controls appear for
              documentation:edit only (portal-api refuses anyone else). See
              docs/online-training.md. */}
          <Route
            path="online-training"
            element={
              <RequireAuth permission="documentation:view">
                <OnlineTrainingListPage />
              </RequireAuth>
            }
          />
          <Route
            path="online-training/:id"
            element={
              <RequireAuth permission="documentation:view">
                <OnlineTrainingEditorPage />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="server"
          element={
            <RequireAuth permission="server:view">
              <ServerStatusPage />
            </RequireAuth>
          }
        />
        {/* Tester — capability-testing runner (last nav tab, after Server).
            The shell gates on `tester:view`; mutating a target and firing a
            live test additionally gate on `tester:run` inside the pages.
            Three sub-tabs: Run / Servers / History. */}
        <Route
          path="tester"
          element={
            <RequireAuth permission="tester:view">
              <TesterLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="run" replace />} />
          <Route path="run" element={<TesterRunPage />} />
          <Route path="servers" element={<TesterServersPage />} />
          <Route path="runs" element={<TesterHistoryPage />} />
        </Route>
        {/* System Admin is a tabbed area (User Management, Otto Bots, AI
            Providers, Security, Change Log) behind one shared shell. `/admin`
            redirects to `/admin/users`; every legacy sub-path — the
            old tabbed shell's `/admin/{dashboard-users,roles,
            crm-accounts,crm-roles,tenants,cross-tenant-users}` URLs —
            redirects to `/admin/users` too so external bookmarks
            survive. The whole area is gated on `admin:users`. */}
        {/* The shell itself is NOT the gate any more, because the two
            surfaces under it answer to DIFFERENT permissions: everything
            dashboard-identity needs `admin:users`, while Reseller Accounts
            needs `crm:resellers` (spec A §6 — "the same gate the existing
            reseller wall uses"). A sales_manager holds the second and not
            the first, so a single shell-level gate would either lock it out
            of reseller management or open the security monitor to it. Two
            pathless gate groups keep each child behind exactly its own
            permission; `AdminLayout` filters the tab strip to match, and the
            outer AppShell RequireAuth still demands a signed-in user. */}
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminIndexRedirect />} />
          <Route
            element={
              <RequireAuth permission="admin:users">
                <Outlet />
              </RequireAuth>
            }
          >
          <Route path="users" element={<SystemAdminLayout />} />
          {/* Otto bots — native System Admin tab (moved from the old
              top-level `/otto`). Shares the `/admin` `admin:users`
              gate + the AdminLayout shell, same as the other tabs. */}
          <Route path="otto" element={<OttoBotsPanel />} />
          {/* AI Providers — native System Admin tab. Shares the `/admin`
              `admin:users` gate + AdminLayout shell. Shows/swaps the
              gateway's active LLM provider. */}
          <Route path="ai-providers" element={<AIProvidersPanel />} />
          {/* System Emails — which admins and salespeople receive each
              staff-facing system email. Shares the `admin:users` gate +
              AdminLayout shell; crm-api owns the lists (it sends the mail). */}
          <Route path="system-emails" element={<SystemEmailsPanel />} />
          {/* Security monitor — native System Admin tab. 24/7 feed of
              unauthorized-access / unauthorized-action events (401/403
              denials, failed logins, 2FA-enrollment blocks) across the
              dashboard + connected services. Shares the `admin:users`
              gate + AdminLayout shell. */}
          <Route path="security" element={<SecurityPanel />} />
          {/* Change Log — native System Admin tab. A calendar of what
              shipped across the five active stack repos, fed by the
              committed `data/changelog.ts` (one entry per push to main).
              Shares the `admin:users` gate + AdminLayout shell. */}
          <Route path="changelog" element={<ChangelogPanel />} />
          </Route>
          {/* Products & Releases and Price List were System Admin tabs until
              2026-09-12. The price book is CRM ▸ Pricing now; the RELEASES
              half is CRM ▸ Releases (`/crm/releases`, its own sidebar entry
              since 2026-09-13), so the old products-releases bookmark lands on
              the releases, which is what that page was mostly used for. Both
              keep the `crm:products_pricing` grant. Redirects sit OUTSIDE the
              admin gate on purpose — a redirect must redirect, and the CRM
              pages do their own guarding. */}
          <Route path="products-releases" element={<Navigate to="/crm/releases" replace />} />
          <Route path="price-list" element={<Navigate to="/crm/pricing" replace />} />
          {/* Reseller Accounts (the PEOPLE inside reseller companies) lived
              here until 2026-09-12; it is the Accounts tab of the CRM reseller
              profile now (`/crm/administration/resellers/:id?tab=accounts`).
              A redirect, outside any admin gate, so the old bookmark lands on
              the reseller list. */}
          <Route path="resellers" element={<Navigate to="/crm/administration/resellers" replace />} />
          {/* Reseller Applications lived here until 2026-09-12; they are the
              second tab of CRM ▸ Reseller Management now (gated on the new
              `crm:reseller_applications` grant, which the CRM page checks).
              Outside the gate above on purpose: a redirect must redirect. */}
          <Route
            path="reseller-applications"
            element={<Navigate to="/crm/administration/resellers?tab=applications" replace />}
          />
          <Route path="reseller-applications/:id" element={<RedirectToResellerApplication />} />
          {/* Legacy deep-links → User Management */}
          <Route
            path="dashboard-users"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="roles"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="crm-accounts"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="crm-roles"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="tenants"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="cross-tenant-users"
            element={<Navigate to="/admin/users" replace />}
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
